import { z } from "zod";

/**
 * Üyelik YAŞAM DÖNGÜSÜ (CLOS-77 `[038]`).
 *
 * Durum alanının kendisi CLOS-199 `[033]` ile doğmuştu — kapasite motoru onu OKUMAK
 * zorundaydı. Komutlar ve geçiş kuralları bu kartın konusu.
 *
 * Durum makinesi `packages/shared` içinde çünkü hem sunucu kapıları hem arayüz aynı
 * cevabı vermek zorunda: "bu üyelik askıya alınabilir mi?" sorusuna iki ayrı yerde iki
 * ayrı cevap verilseydi, düğmenin görünür olduğu ama isteğin reddedildiği bir yüzey
 * doğardı.
 */
export const MEMBERSHIP_STATUSES = ["active", "suspended", "ended"] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

/**
 * İZİN VERİLEN geçişler.
 *
 * Kartın ilk cümlesi — *"Yalnız aktif üyelik askıya alınabilir veya sona erdirilebilir;
 * yalnız askıdaki üyelik yeniden aktifleştirilebilir."* — birebir uygulandığında bir
 * sonuç doğuruyordu: **askıdaki birini çıkarmak için önce yeniden aktifleştirmek**, yani
 * erişimini geri vermek gerekiyordu. Bu iki nedenle sorunluydu: (1) güvenlik yönü ters —
 * askı çoğu kez bir güvenlik incelemesiyle başlar ve çıkarmak için erişimi geri açmak
 * gerekiyordu; (2) plan düşüşünde kilitlenme — yeniden aktifleştirme kapasiteye tabi
 * olduğu için sınırını aşmış organizasyon askıdaki üyeyi ne geri alabiliyor ne
 * çıkarabiliyordu, koltuğu belirsiz süre tutuyordu.
 *
 * Bilete `ACIK-KARAR` olarak yazıldı ve ürün sahibi **(B) `suspended → ended` geçişini
 * açtı** (7 Ağustos 2026). Artık askıdaki üyelik doğrudan sona erdirilebiliyor;
 * yeniden-aktifleştirme dayatması kalktı.
 *
 * `ended` TERMİNAL: sona ermiş üyelik geri alınmaz, yeni bir davet gerekir. Geri
 * alınabilseydi "bu kişi ne zamandan beri üye?" sorusunun tek bir cevabı olmazdı.
 */
export const MEMBERSHIP_TRANSITIONS: Record<MembershipStatus, readonly MembershipStatus[]> = {
  active: ["suspended", "ended"],
  suspended: ["active", "ended"],
  ended: [],
};

export function nextMembershipStatuses(from: MembershipStatus): readonly MembershipStatus[] {
  return MEMBERSHIP_TRANSITIONS[from];
}

/** Aynı duruma "geçiş" bir geçiş değildir: idempotentlik komut katmanının işi. */
export function canTransitionMembership(from: MembershipStatus, to: MembershipStatus): boolean {
  return MEMBERSHIP_TRANSITIONS[from].includes(to);
}

/**
 * Çalışma alanı ERİŞİMİ — kartın *"askıdaki kullanıcı erişemez"* kriteri.
 *
 * Tek fonksiyon, tek cevap. Kapı (`requireTenant`) ve listeleme aynı yerden soruyor;
 * kural iki yerde yazılsaydı biri güncellenip diğeri unutulduğunda askıdaki kullanıcı
 * bir kapıdan geçmeye devam ederdi.
 *
 * **Geçmiş ayrı bir eksen:** erişimin kapanması kayıtların silinmesi değildir. Kullanıcı
 * eski işlemlerin aktörü olarak audit'te durmaya devam eder (kart: *"geçmişi korunur"*).
 */
export function grantsWorkspaceAccess(status: MembershipStatus): boolean {
  return status === "active";
}

/**
 * Askı GEREKÇE kategorileri konfigürasyondan gelir (kartın açık kararı:
 * *"üyelik askı gerekçe kategorileri ... konfigüre edilecektir"*).
 *
 * Koda gömülselerdi yeni bir kategori eklemek sürüm gerektirirdi; oysa bunlar ürünün
 * mantığı değil, organizasyonun idari sözlüğü.
 */
export const membershipConfigSchema = z.object({
  suspensionReasons: z
    .array(
      z.object({
        code: z
          .string()
          .min(1)
          .max(64)
          .regex(/^[a-z][a-z0-9_]*$/, "Gerekçe kodu küçük harf ve alt çizgi olmalıdır"),
        label: z.string().min(1).max(120),
      }),
    )
    .min(1, "En az bir askı gerekçesi tanımlanmalıdır")
    .refine(
      (liste) => new Set(liste.map((r) => r.code)).size === liste.length,
      "Askı gerekçe kodları benzersiz olmalıdır",
    ),
});

export type MembershipConfig = z.infer<typeof membershipConfigSchema>;
export type SuspensionReason = MembershipConfig["suspensionReasons"][number];

/**
 * Askıya alma girdisi.
 *
 * `reason` burada yalnız BİÇİM olarak doğrulanıyor; geçerli kodlar konfigürasyondan
 * geldiği için kümesi derleme anında bilinmiyor. Kod kümesi kontrolü servis katmanında
 * ve reddi yine 422 alan hatası olarak dönüyor — kullanıcı için fark yok.
 */
export const suspendMembershipSchema = z.object({
  reason: z
    .string({ error: "Askı gerekçesi zorunludur" })
    .min(1, "Askı gerekçesi zorunludur")
    .max(64),
  /** Serbest metin not: kategori yetmediğinde bağlam bırakmak için. */
  note: z.string().max(500, "Not en fazla 500 karakter olabilir").optional(),
});

export type SuspendMembershipInput = z.infer<typeof suspendMembershipSchema>;

/** Sona erdirme de gerekçe alır: "neden çıkarıldı?" sorusu askıdan daha sık sorulur. */
export const endMembershipSchema = suspendMembershipSchema;
